import { useEffect, useState } from "react";
import { http, API } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Admin section for the Beta Feedback dashboard. Renders inside AdminPage
 * next to the existing system-health + recent-feedback sections.
 *   - Overview cards        (cohort counts + onboarded/not_onboarded)
 *   - Response-rates table  (per-milestone eligible vs completed)
 *   - Top-features table    (feedback counts + avg ratings)
 *   - CSV export dropdown
 */
const MILESTONE_LABELS = {
  onboarding:     "Mandatory onboarding",
  day7_checkin:   "Day 7 check-in",
  day30_product:  "Day 30 product",
  final_pmf:      "Final PMF",
};

export default function AdminBetaFeedback() {
  const [overview, setOverview] = useState(null);
  const [rates, setRates] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportKind, setExportKind] = useState("feedback");

  const load = async () => {
    setLoading(true);
    try {
      const [o, r, f] = await Promise.all([
        http.get("/admin/beta-feedback/overview"),
        http.get("/admin/beta-feedback/response-rates"),
        http.get("/admin/beta-feedback/top-features"),
      ]);
      setOverview(o.data);
      setRates(r.data?.rows || []);
      setFeatures(f.data?.rows || []);
    } catch (e) {
      toast.error("Could not load beta feedback dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const download = () => {
    const url = `${API}/admin/beta-feedback/export.csv?kind=${exportKind}`;
    // Cookie auth is same-origin → just open in a new tab so the browser
    // fetches with credentials automatically.
    window.open(url, "_blank");
  };

  const pct = (n) => (n == null ? "—" : `${Math.round(n * 100)}%`);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-beta-feedback">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display font-bold text-lg">Beta feedback dashboard</div>
          <div className="text-xs text-slate-500 mt-0.5">Onboarding, milestones, per-feature ratings.</div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={exportKind}
            onChange={(e) => setExportKind(e.target.value)}
            data-testid="beta-feedback-export-kind"
            className="h-9 px-3 rounded-full border border-slate-200 bg-white text-xs font-semibold"
          >
            <option value="feedback">Feedback</option>
            <option value="milestones">Milestones</option>
            <option value="onboarding">Onboarding</option>
          </select>
          <Button size="sm" variant="outline" onClick={download} className="rounded-full" data-testid="beta-feedback-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> CSV
          </Button>
          <Button size="sm" variant="ghost" onClick={load} className="rounded-full" data-testid="beta-feedback-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="beta-feedback-overview">
          <StatCard label="Total beta users"   value={overview.total} testId="stat-total" />
          <StatCard label="Active"             value={overview.active} testId="stat-active" />
          <StatCard label="Onboarded"          value={overview.onboarded} testId="stat-onboarded" />
          <StatCard label="Not onboarded"      value={overview.not_onboarded} testId="stat-not-onboarded" />
          <StatCard label="Standard 60-day"    value={overview.standard_60_day} testId="stat-standard" />
          <StatCard label="Founding · solo"    value={overview.founding_solo} testId="stat-founding-solo" />
          <StatCard label="Founding · clinic"  value={overview.founding_clinic} testId="stat-founding-clinic" />
          <StatCard label="Clinic vs Solo"     value={`${overview.clinic} · ${overview.solo}`} testId="stat-clinic-solo" />
        </div>
      )}

      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Milestone response rates</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="beta-feedback-rates-table">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Milestone</th>
                <th className="py-2 pr-4">Eligible</th>
                <th className="py-2 pr-4">Completed</th>
                <th className="py-2 pr-4">Response rate</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.milestone} className="border-b border-slate-100" data-testid={`rate-row-${r.milestone}`}>
                  <td className="py-2 pr-4 font-medium text-slate-800">{MILESTONE_LABELS[r.milestone] || r.milestone}</td>
                  <td className="py-2 pr-4 text-slate-600">{r.eligible}</td>
                  <td className="py-2 pr-4 text-slate-600">{r.completed}</td>
                  <td className="py-2 pr-4 font-semibold text-teal-700">{pct(r.rate)}</td>
                </tr>
              ))}
              {rates.length === 0 && !loading && (
                <tr><td colSpan={4} className="py-4 text-slate-500 text-sm">No response data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Top features by feedback + rating</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="beta-feedback-features-table">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Feature</th>
                <th className="py-2 pr-4">Feedback total</th>
                <th className="py-2 pr-4">Bugs</th>
                <th className="py-2 pr-4">Feature reqs</th>
                <th className="py-2 pr-4">UX issues</th>
                <th className="py-2 pr-4">Avg rating</th>
                <th className="py-2 pr-4"># ratings</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f) => (
                <tr key={f.feature} className="border-b border-slate-100" data-testid={`feature-row-${f.feature}`}>
                  <td className="py-2 pr-4 font-medium text-slate-800">{f.label}</td>
                  <td className="py-2 pr-4 text-slate-600">{f.feedback_total}</td>
                  <td className="py-2 pr-4 text-rose-600">{f.feedback_by_category?.bug || 0}</td>
                  <td className="py-2 pr-4 text-sky-600">{f.feedback_by_category?.feature_request || 0}</td>
                  <td className="py-2 pr-4 text-amber-600">{f.feedback_by_category?.ux_issue || 0}</td>
                  <td className="py-2 pr-4 font-semibold text-teal-700">{f.avg_rating ?? "—"}</td>
                  <td className="py-2 pr-4 text-slate-500">{f.rating_count}</td>
                </tr>
              ))}
              {features.length === 0 && !loading && (
                <tr><td colSpan={7} className="py-4 text-slate-500 text-sm">No feature data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="mt-1 text-xl font-extrabold text-slate-900">{value}</div>
    </div>
  );
}
